// Helpers for rendering rows from public.stock_descriptions cleanly.

// Uppercase the first letter of a string if it's a lowercase letter. Used
// after stripNamePrefix because some brand-name prefixes (e.g. "nVent") cause
// the stripped remainder to start lowercase, which reads as a typo. Leaves
// already-capital, numeric, and punctuation starts alone.
function capitalizeFirst(s) {
  if (!s) return s
  const c = s.charAt(0)
  if (c >= 'a' && c <= 'z') return c.toUpperCase() + s.slice(1)
  return s
}

// Strip a redundant company-name prefix from a one_liner. 158 of the 266
// seeded rows lead with the company's own name + em-dash (or hyphen), e.g.
// "Coherent — lasers and photonic components for AI optics". When the
// surrounding UI already shows the company name, that prefix is visible
// noise, so we drop it. Many other rows use the same em-dash for a real
// descriptive phrase (e.g. "California utility — limited AI exposure" on
// PCG), so we only strip when the prefix is fuzzy-matchable against the
// known stock name.
//
// Returns the cleaned string (capitalized first letter), or the original
// input when no strip applies.
export function stripNamePrefix(oneLiner, stockName) {
  if (!oneLiner) return oneLiner
  const m = oneLiner.match(/^([^—\-]{1,60})\s*[—\-]\s+(.+)$/)
  if (!m) return capitalizeFirst(oneLiner)
  const prefix = m[1].trim()
  const rest   = m[2].trim()
  if (!rest) return capitalizeFirst(oneLiner)

  const name = (stockName || '').trim()
  if (!name) return capitalizeFirst(oneLiner)

  const norm = (s) => s.toLowerCase().replace(/[.,&]/g, '').replace(/\s+/g, ' ').trim()
  const np = norm(prefix)
  const nn = norm(name)
  if (!np || !nn) return capitalizeFirst(oneLiner)

  if (np === nn) return capitalizeFirst(rest)
  if (nn.startsWith(np)) return capitalizeFirst(rest)                   // "Hubbell" matches "Hubbell Inc."
  if (np.startsWith(nn) && nn.length >= 3) return capitalizeFirst(rest) // "NetApp Inc" matches "NetApp"
  const npWord = np.split(' ')[0]
  const nnWord = nn.split(' ')[0]
  // Match on first word when both >= 4 chars (avoids "AI" / "S&P" collisions).
  if (npWord && nnWord && npWord === nnWord && npWord.length >= 4) return capitalizeFirst(rest)
  // Stem match for period-abbreviated names. stocks.name often abbreviates
  // ("Appl. Optoelectronics") while one_liner uses the full word ("Applied
  // Optoelectronics"). If the shorter first-word is a prefix of the longer
  // and is >= 3 chars (e.g. "appl" ⊂ "applied", "intl" ⊂ "international"),
  // and the second words match, treat them as the same company.
  if (npWord && nnWord && npWord !== nnWord) {
    const short = npWord.length < nnWord.length ? npWord : nnWord
    const long  = npWord.length < nnWord.length ? nnWord : npWord
    if (short.length >= 3 && long.startsWith(short)) {
      const npRest = np.split(' ').slice(1).join(' ')
      const nnRest = nn.split(' ').slice(1).join(' ')
      // Only commit to the match if the rest of each (after the first word)
      // also overlaps — otherwise "Appl." could collide with "Apple Inc.".
      if (npRest && nnRest && (npRest === nnRest || npRest.startsWith(nnRest) || nnRest.startsWith(npRest))) {
        return capitalizeFirst(rest)
      }
    }
  }

  return capitalizeFirst(oneLiner)
}
